import React, { useRef, useState } from 'react';
import styles from './ImagePicker.module.css';

interface ImagePickerProps {
  /** Current image as a data URL or external URL. */
  value: string | null;
  /** Called with the data URL when a file is selected, or null when cleared. */
  onChange: (dataUrl: string | null) => void;
  /** Max raw file size in MB before base64-encoding. Default 1 MB. */
  maxSizeMB?: number;
  label?: string;
}

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

export const ImagePicker: React.FC<ImagePickerProps> = ({
  value,
  onChange,
  maxSizeMB = 1,
  label = 'Product Image',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  const readFile = (file: File) => {
    setError(null);
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Only PNG, JPEG, WebP, or GIF images are supported');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`Image must be ${maxSizeMB} MB or smaller`);
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setError('Failed to read file');
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') onChange(result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) readFile(file);
    // reset so re-selecting same file re-fires onChange
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) readFile(file);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setError(null);
    onChange(null);
  };

  return (
    <div className={styles.wrapper}>
      <span className={styles.label}>{label}</span>

      <div
        className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ''} ${value ? styles.dropzoneFilled : ''}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
      >
        {value ? (
          <>
            <img src={value} alt="Preview" className={styles.preview} />
            <button type="button" className={styles.clearBtn} onClick={handleClear} aria-label="Remove image">
              ×
            </button>
          </>
        ) : (
          <div className={styles.placeholder}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
              <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
            </svg>
            <span className={styles.placeholderText}>Click or drop image here</span>
            <span className={styles.placeholderHint}>PNG, JPEG, WebP, GIF · max {maxSizeMB} MB</span>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileChange}
          className={styles.hiddenInput}
        />
      </div>

      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
};

export default ImagePicker;
